import {
  faPause,
  faSpinner,
  faVolumeHigh,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { toast } from 'react-toastify';
import TextToSpeech from '../../Components/TextToSpeech';
import { AudioPlayerState } from '../../Context/AudioContext';
import { Paragraph } from '../Types/ArticleTypes';

const PARAGAPHS_TO_PRELOAD_AUDIO = 1;

interface ParagraphReadAloudProps {
  paragraph: Paragraph;
  playbackParagraph?: Paragraph;
  paragraphs: Paragraph[];
  playbackState: AudioPlayerState;
  isAudioLoaded: boolean;
  audioManager: any;
  articleId: string;
  onPlayClick: (paragraph: Paragraph) => void;
  onPauseClick: () => void;
  onReadyForPlayback: () => void;
  onPlaybackMarkerChange: (
    marker: { text: string; startIndex: number; endIndex: number } | null,
  ) => void;
  onPlaybackParagraphChange: (paragraph: Paragraph) => void;
  onStop: () => void;
}

export default function ParagraphReadAloud({
  paragraph,
  playbackParagraph,
  paragraphs,
  playbackState,
  isAudioLoaded,
  audioManager,
  articleId,
  onPlayClick,
  onPauseClick,
  onReadyForPlayback,
  onPlaybackMarkerChange,
  onPlaybackParagraphChange,
  onStop,
}: ParagraphReadAloudProps) {
  if (!playbackParagraph) return null;

  const isCurrentParagraph =
    playbackParagraph.startIndex === paragraph.startIndex;

  return (
    <div className="w-8 flex-shrink-0 max-sm:w-6">
      <TextToSpeech
        key={`article-${articleId}-paragraph-${paragraph.startIndex}-${paragraph.endIndex}`}
        groupId={`${articleId}-passage`}
        descriptorUrl={`${window.env.REACT_APP_API_URL}/tts/article/${articleId}?startIndex=${paragraph.startIndex}&endIndex=${paragraph.endIndex}`}
        manager={audioManager}
        analyticsContext={{
          type: 'paragraph',
          articleId: articleId,
          paragraphIndex: paragraphs.indexOf(paragraph),
        }}
        state={
          !paragraph.section.isBlurred &&
          paragraph.section.startIndex ===
            playbackParagraph?.section.startIndex &&
          paragraph.startIndex === playbackParagraph?.startIndex
            ? playbackState
            : AudioPlayerState.Stop
        }
        preload={
          playbackParagraph &&
          paragraphs.indexOf(paragraph) <=
            paragraphs.indexOf(playbackParagraph) + PARAGAPHS_TO_PRELOAD_AUDIO
        }
        onPlay={() => {
          onReadyForPlayback();
        }}
        onCancel={() => {
          onStop();
        }}
        onEnd={() => {
          const nextParagraph = paragraphs.find(
            (s) => s.startIndex >= playbackParagraph.endIndex,
          );
          if (nextParagraph) {
            console.debug(`Next paragraph`, nextParagraph);
            onPlaybackParagraphChange(nextParagraph);
          } else {
            console.debug('Playback ended');
            onStop();
          }
        }}
        onWordStart={(word, startIndex, endIndex) => {
          onPlaybackMarkerChange({
            text: word,
            startIndex: startIndex + paragraph.startIndex,
            endIndex: endIndex + paragraph.startIndex,
          });
        }}
        onError={() => {
          onStop();
          toast.error('Error loading audio');
        }}
      />
      {isCurrentParagraph ? (
        isAudioLoaded ? (
          playbackState === AudioPlayerState.Play ? (
            <div
              className="cursor-pointer flex justify-start items-start pt-1 w-8 h-8 text-center text-gray-600 whitespace-nowrap"
              onClick={onPauseClick}
            >
              <FontAwesomeIcon
                icon={faPause}
                color={'#0D6EFD'}
                className="align-top h-5 w-5 max-sm:h-4 max-sm:w-4"
              />
            </div>
          ) : (
            <div
              className="cursor-pointer flex justify-start items-start pt-1 w-8 h-8 text-center text-gray-600 whitespace-nowrap"
              onClick={() => onPlayClick(playbackParagraph)}
            >
              <FontAwesomeIcon
                icon={faVolumeHigh}
                color={'#0D6EFD'}
                className="align-top h-5 w-5 max-sm:h-4 max-sm:w-4"
              />
            </div>
          )
        ) : (
          <div className="flex justify-start items-start pt-1 w-8 h-8 text-center text-gray-600 whitespace-nowrap">
            <FontAwesomeIcon
              icon={faSpinner}
              color={'grey'}
              className="animate-spin align-top h-5 w-5 max-sm:h-4 max-sm:w-4"
            />
          </div>
        )
      ) : (
        <div
          className="cursor-pointer flex justify-start items-start pt-1 w-8 h-8 text-center text-gray-600 whitespace-nowrap"
          onClick={() => onPlayClick(paragraph)}
        >
          <FontAwesomeIcon
            icon={faVolumeHigh}
            color={'#0D6EFD'}
            className="align-top h-5 w-5 max-sm:h-4 max-sm:w-4"
          />
        </div>
      )}
    </div>
  );
}
